import { useState, useEffect } from 'react';
import { ChevronRight, Check, ArrowLeft, Radio } from 'lucide-react';
import { apiFetch } from '../api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DbEntry { id: number; name: string; }
interface Company { company_name: string; company_api_id: number; db_id: number; }
interface Area { area_name: string; area_api_id: number; area_id: number; }
interface Machine { machine_code: string; machine_name: string; }
interface Point { point_index: number; point_name: string | null; }

type AxisLabel = 'H' | 'V' | 'A';
interface AxisMapping { x: AxisLabel; y: AxisLabel; z: AxisLabel; }

type Step = 'type' | 'code' | 'location' | 'axes' | 'channels' | 'variables' | 'done';

// The three sensor classes, matching devices.sensor_class exactly — the values
// are a DB CHECK constraint (devices_sensor_class_valid), not free text.
type SensorClass = 'vibration' | 'gp_analog' | 'gp_current';
// Matches gp_channel_config.signal_type's CHECK (gp_channel_config_signal_type_valid).
type SignalType = '0-10vdc' | '4-20mA' | 'current';

// How one channel is assigned. 'point' = the sensor's own point; 'other_point' =
// another point on the SAME machine; 'machine' = machine-level (point_index NULL
// on the config row).
type ChannelAssign = 'point' | 'other_point' | 'machine';

interface ChannelDraft {
  channel_index: number;
  enabled: boolean;
  assign: ChannelAssign;
  other_point_index: number | null;
  variable_name: string;
  eng_unit: string;
  // Kept as STRINGS while editing: an empty numeric input is not 0, and coercing
  // it early would silently scale against a range the operator never entered.
  in_min: string;
  in_max: string;
  eng_min: string;
  eng_max: string;
  // Test box: the input, and whatever the SERVER said it scales to.
  testInput: string;
  preview: number | null;
  previewError: string;
  previewPending: boolean;
}

// Default input range per signal type. 4-20 mA's offset zero is why it exists —
// a broken wire reads 0 mA, which is distinguishable from a valid low reading.
// Pre-filled, and overridable on the variable step.
const SIGNAL_DEFAULT_RANGE: Record<SignalType, { in_min: string; in_max: string }> = {
  '0-10vdc': { in_min: '0', in_max: '10' },
  '4-20mA':  { in_min: '4', in_max: '20' },
  'current': { in_min: '',  in_max: '' },
};

function newChannel(idx: number, signal: SignalType): ChannelDraft {
  const r = SIGNAL_DEFAULT_RANGE[signal];
  return {
    channel_index: idx,
    enabled: idx === 1,
    assign: 'point',
    other_point_index: null,
    variable_name: '',
    eng_unit: signal === 'current' ? 'A' : '',
    in_min: r.in_min, in_max: r.in_max, eng_min: '', eng_max: '',
    testInput: '', preview: null, previewError: '', previewPending: false,
  };
}

// Phantom codes are 9-digit integers
function isValidCode(code: string): boolean {
  return /^\d{9}$/.test(code.trim());
}

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

// The step sequence BRANCHES on sensor class. Vibration is the existing flow with
// the type step in front of it — nothing after 'type' changes for it, which is
// the regression surface this whole step has to leave alone.
const VIBRATION_STEPS: { id: Step; label: string }[] = [
  { id: 'type',     label: 'Sensor type' },
  { id: 'code',     label: 'Device code' },
  { id: 'location', label: 'Select point' },
  { id: 'axes',     label: 'Axis mapping' },
];

const GP_STEPS: { id: Step; label: string }[] = [
  { id: 'type',      label: 'Sensor type' },
  { id: 'code',      label: 'Device code' },
  { id: 'location',  label: 'Select point' },
  { id: 'channels',  label: 'Channels' },
  { id: 'variables', label: 'Variables' },
];

function stepsFor(cls: SensorClass) {
  return cls === 'vibration' ? VIBRATION_STEPS : GP_STEPS;
}

function StepBar({ current, steps }: { current: Step; steps: { id: Step; label: string }[] }) {
  const idx = steps.findIndex(s => s.id === current);
  return (
    <div className="flex items-center gap-0 mb-8">
      {steps.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <div key={s.id} className="flex items-center gap-0">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                done   ? 'bg-[#00D4BE] text-white' :
                active ? 'bg-[#00D4BE]/20 text-[#00D4BE] border-2 border-[#00D4BE]' :
                         'bg-gray-100 text-gray-400'
              }`}>
                {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-sm font-medium ${active ? 'inline' : 'hidden sm:inline'} ${active ? 'text-gray-800' : done ? 'text-[#00D4BE]' : 'text-gray-400'}`}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`w-6 sm:w-12 h-0.5 mx-1.5 sm:mx-3 ${done ? 'bg-[#00D4BE]' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cascading select helper
// ---------------------------------------------------------------------------

function SelectRow<T>({
  label,
  value,
  options,
  disabled,
  onSelect,
  getKey,
  getLabel,
}: {
  label: string;
  value: T | null;
  options: T[];
  disabled?: boolean;
  onSelect: (v: T) => void;
  getKey: (v: T) => string;
  getLabel: (v: T) => string;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</label>
      <select
        disabled={disabled || options.length === 0}
        value={value ? getKey(value) : ''}
        onChange={e => {
          const found = options.find(o => getKey(o) === e.target.value);
          if (found) onSelect(found);
        }}
        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00D4BE]/60 transition-colors disabled:opacity-50"
      >
        <option value="">Select {label.toLowerCase()}…</option>
        {options.map(o => (
          <option key={getKey(o)} value={getKey(o)}>{getLabel(o)}</option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 0 — Sensor type (new)
// ---------------------------------------------------------------------------
// Defaults to Vibration, which is today's flow. Picking it changes nothing about
// the remaining steps — that is deliberate: this step is a fork in front of an
// unchanged path, not a rewrite of it.

const SENSOR_TYPES: { id: SensorClass; title: string; blurb: string }[] = [
  { id: 'vibration',  title: 'Vibration',
    blurb: 'Standard triaxial vibration sensor (H/V/A). The default.' },
  { id: 'gp_analog',  title: 'General Purpose',
    blurb: 'Analog transducer — pressure, flow, level. 0-10 VDC or 4-20 mA, scaled to engineering units.' },
  { id: 'gp_current', title: 'Current Clamp',
    blurb: 'Motor current sensor. Reports Amps directly — no scaling needed.' },
];

const SIGNAL_TYPES: { id: SignalType; title: string; blurb: string }[] = [
  { id: '0-10vdc', title: '0-10 VDC', blurb: 'Voltage output. Input range pre-fills 0–10 V.' },
  { id: '4-20mA',  title: '4-20 mA',  blurb: 'Current loop. Input range pre-fills 4–20 mA — the offset zero is what makes a broken wire (0 mA) distinguishable from a valid low reading.' },
];

function StepType({
  sensorClass,
  signalType,
  onChangeClass,
  onChangeSignal,
  onNext,
}: {
  sensorClass: SensorClass;
  signalType: SignalType;
  onChangeClass: (c: SensorClass) => void;
  onChangeSignal: (s: SignalType) => void;
  onNext: () => void;
}) {
  return (
    <div className="max-w-lg">
      <h2 className="text-base font-semibold text-gray-800 mb-1">What kind of sensor is this?</h2>
      <p className="text-sm text-gray-500 mb-5">
        Vibration is the standard sensor. The other two are non-vibration channels
        that report engineering values.
      </p>

      <div className="space-y-2.5 mb-5">
        {SENSOR_TYPES.map(t => {
          const active = sensorClass === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChangeClass(t.id)}
              className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                active
                  ? 'bg-[#00D4BE]/5 border-[#00D4BE] ring-1 ring-[#00D4BE]/30'
                  : 'bg-white border-gray-200 hover:border-[#00D4BE]/40'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-sm font-semibold ${active ? 'text-[#00D4BE]' : 'text-gray-800'}`}>
                  {t.title}
                </span>
                {t.id === 'vibration' && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                    default
                  </span>
                )}
                {active && <Check className="w-4 h-4 text-[#00D4BE] ml-auto" />}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{t.blurb}</p>
            </button>
          );
        })}
      </div>

      {sensorClass === 'gp_analog' && (
        <div className="mb-5 pl-4 border-l-2 border-[#00D4BE]/30">
          <label className="block text-xs text-gray-500 uppercase tracking-wide mb-2">Signal type</label>
          <div className="space-y-2">
            {SIGNAL_TYPES.map(s => {
              const active = signalType === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onChangeSignal(s.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                    active
                      ? 'bg-[#00D4BE]/5 border-[#00D4BE] text-gray-800'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-[#00D4BE]/40'
                  }`}
                >
                  <span className="font-medium">{s.title}</span>
                  <p className="text-xs text-gray-500 mt-0.5">{s.blurb}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onNext}
        className="flex items-center gap-2 px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors"
      >
        Next <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Device code
// ---------------------------------------------------------------------------

function StepCode({
  code,
  onChange,
  onNext,
}: {
  code: string;
  onChange: (v: string) => void;
  onNext: () => void;
}) {
  const valid = isValidCode(code);
  const touched = code.length > 0;

  return (
    <div className="max-w-sm">
      <h2 className="text-base font-semibold text-gray-800 mb-1">Enter device code</h2>
      <p className="text-sm text-gray-500 mb-5">
        The phantom code is the 9-digit serial number printed on the sensor.
      </p>
      <div className="space-y-4">
        <div>
          <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Phantom Code</label>
          <div className="relative">
            <Radio className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={code}
              onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 9))}
              placeholder="e.g. 189303885"
              maxLength={9}
              className={`w-full pl-9 pr-3 py-2.5 bg-gray-50 border rounded-lg text-sm font-mono focus:outline-none transition-colors ${
                touched && !valid ? 'border-red-300 focus:border-red-400' :
                valid ? 'border-green-300 focus:border-green-400' :
                'border-gray-200 focus:border-[#00D4BE]/60'
              }`}
            />
            {valid && (
              <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
            )}
          </div>
          {touched && !valid && (
            <p className="text-xs text-red-500 mt-1">Must be exactly 9 digits.</p>
          )}
        </div>
        <button
          type="button"
          onClick={onNext}
          disabled={!valid}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors disabled:opacity-50"
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Select point from hierarchy
// ---------------------------------------------------------------------------

function StepLocation({
  onSelect,
  onBack,
}: {
  onSelect: (params: { db: DbEntry; company: Company; area: Area; machine: Machine; point: Point }) => void;
  onBack: () => void;
}) {
  const [databases, setDatabases] = useState<DbEntry[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [points, setPoints] = useState<Point[]>([]);

  const [selDb, setSelDb] = useState<DbEntry | null>(null);
  const [selCo, setSelCo] = useState<Company | null>(null);
  const [selArea, setSelArea] = useState<Area | null>(null);
  const [selMachine, setSelMachine] = useState<Machine | null>(null);
  const [selPoint, setSelPoint] = useState<Point | null>(null);

  useEffect(() => { apiFetch<DbEntry[]>('/setup/databases').then(setDatabases).catch(() => {}); }, []);

  useEffect(() => {
    if (!selDb) { setCompanies([]); setSelCo(null); return; }
    apiFetch<Company[]>(`/setup/companies?db_id=${selDb.id}`)
      .then(d => { setCompanies(d); setSelCo(null); setAreas([]); setSelArea(null); setMachines([]); setSelMachine(null); setPoints([]); setSelPoint(null); })
      .catch(() => {});
  }, [selDb]);

  useEffect(() => {
    if (!selDb || !selCo) { setAreas([]); setSelArea(null); return; }
    apiFetch<Area[]>(`/setup/areas?db_id=${selDb.id}&company_name=${encodeURIComponent(selCo.company_name)}`)
      .then(d => { setAreas(d); setSelArea(null); setMachines([]); setSelMachine(null); setPoints([]); setSelPoint(null); })
      .catch(() => {});
  }, [selCo, selDb]);

  useEffect(() => {
    if (!selDb || !selCo || !selArea) { setMachines([]); setSelMachine(null); return; }
    apiFetch<Machine[]>(`/setup/machines-list?db_id=${selDb.id}&company_name=${encodeURIComponent(selCo.company_name)}&area_name=${encodeURIComponent(selArea.area_name)}`)
      .then(d => { setMachines(d); setSelMachine(null); setPoints([]); setSelPoint(null); })
      .catch(() => {});
  }, [selArea, selCo, selDb]);

  useEffect(() => {
    if (!selMachine) { setPoints([]); setSelPoint(null); return; }
    apiFetch<Point[]>(`/setup/points-list?machine_code=${selMachine.machine_code}`)
      .then(d => { setPoints(d); setSelPoint(null); })
      .catch(() => {});
  }, [selMachine]);

  const canProceed = !!(selDb && selCo && selArea && selMachine && selPoint);

  return (
    <div className="max-w-sm">
      <h2 className="text-base font-semibold text-gray-800 mb-1">Select measurement point</h2>
      <p className="text-sm text-gray-500 mb-5">
        Choose which machine point this sensor is installed on.
      </p>
      <div className="space-y-3">
        <SelectRow
          label="Database"
          value={selDb}
          options={databases}
          onSelect={v => setSelDb(v)}
          getKey={d => String(d.id)}
          getLabel={d => d.name}
        />
        <SelectRow
          label="Company"
          value={selCo}
          options={companies}
          disabled={!selDb}
          onSelect={v => setSelCo(v)}
          getKey={c => c.company_name}
          getLabel={c => c.company_name}
        />
        <SelectRow
          label="Area"
          value={selArea}
          options={areas}
          disabled={!selCo}
          onSelect={v => setSelArea(v)}
          getKey={a => String(a.area_id)}
          getLabel={a => a.area_name}
        />
        <SelectRow
          label="Machine"
          value={selMachine}
          options={machines}
          disabled={!selArea}
          onSelect={v => setSelMachine(v)}
          getKey={m => m.machine_code}
          getLabel={m => m.machine_name}
        />
        <SelectRow
          label="Point"
          value={selPoint}
          options={points}
          disabled={!selMachine}
          onSelect={v => setSelPoint(v)}
          getKey={p => String(p.point_index)}
          getLabel={p => p.point_name ? `${p.point_index} — ${p.point_name}` : `Point ${p.point_index}`}
        />

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            type="button"
            disabled={!canProceed}
            onClick={() => onSelect({ db: selDb!, company: selCo!, area: selArea!, machine: selMachine!, point: selPoint! })}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors disabled:opacity-50"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Axis mapping
// ---------------------------------------------------------------------------

const AXIS_OPTIONS: AxisLabel[] = ['H', 'V', 'A'];

const AXIS_DESCRIPTIONS: Record<AxisLabel, string> = {
  H: 'Horizontal',
  V: 'Vertical',
  A: 'Axial',
};

function AxisSelect({
  channel,
  value,
  onChange,
  usedBy,
}: {
  channel: 'X' | 'Y' | 'Z';
  value: AxisLabel;
  onChange: (v: AxisLabel) => void;
  usedBy: Record<AxisLabel, string>;
}) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-gray-100 last:border-0">
      <div className="w-16 text-center">
        <span className="text-2xl font-bold text-gray-700">{channel}</span>
        <p className="text-xs text-gray-400">Channel</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
      <div className="flex gap-2 flex-1">
        {AXIS_OPTIONS.map(axis => {
          const takenBy = usedBy[axis];
          const isTaken = takenBy && takenBy !== channel;
          const isSelected = value === axis;
          return (
            <button
              key={axis}
              type="button"
              disabled={!!isTaken}
              onClick={() => onChange(axis)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors border ${
                isSelected
                  ? 'bg-[#00D4BE] text-white border-[#00D4BE]'
                  : isTaken
                  ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-[#00D4BE]/40 hover:text-[#00D4BE]'
              }`}
            >
              {axis}
              <span className={`block text-xs font-normal ${isSelected ? 'text-white/80' : 'text-gray-400'}`}>
                {AXIS_DESCRIPTIONS[axis]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepAxes({
  mapping,
  onChange,
  onBack,
  onSave,
  saving,
}: {
  mapping: AxisMapping;
  onChange: (m: AxisMapping) => void;
  onBack: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  // Build reverse map: axis label → which channel has it
  const usedBy: Record<AxisLabel, string> = {
    H: Object.entries(mapping).find(([, v]) => v === 'H')?.[0]?.toUpperCase() ?? '',
    V: Object.entries(mapping).find(([, v]) => v === 'V')?.[0]?.toUpperCase() ?? '',
    A: Object.entries(mapping).find(([, v]) => v === 'A')?.[0]?.toUpperCase() ?? '',
  };

  const setChannel = (ch: 'x' | 'y' | 'z', axis: AxisLabel) => {
    // Swap if target axis already assigned to another channel
    const current = mapping[ch];
    const swapCh = (Object.entries(mapping) as [string, AxisLabel][]).find(([k, v]) => v === axis && k !== ch)?.[0] as 'x' | 'y' | 'z' | undefined;
    const next = { ...mapping, [ch]: axis };
    if (swapCh) next[swapCh] = current;
    onChange(next);
  };

  return (
    <div className="max-w-sm">
      <h2 className="text-base font-semibold text-gray-800 mb-1">Configure axis mapping</h2>
      <p className="text-sm text-gray-500 mb-5">
        Map each sensor channel to the physical axis direction on this machine.
      </p>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 mb-5">
        <AxisSelect channel="X" value={mapping.x} onChange={v => setChannel('x', v)} usedBy={usedBy} />
        <AxisSelect channel="Y" value={mapping.y} onChange={v => setChannel('y', v)} usedBy={usedBy} />
        <AxisSelect channel="Z" value={mapping.z} onChange={v => setChannel('z', v)} usedBy={usedBy} />
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save Device'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3a (GP) — channel assignment
// ---------------------------------------------------------------------------
// A GP sensor has up to four INDEPENDENT physical inputs, so this is where
// today's single-point assignment expands into per-channel assignment. The point
// picker is the same /setup/points-list the location step uses, scoped to the
// machine already chosen — assignment moves a channel within its machine
// (plan §2.4), it does not relocate the sensor.

function StepChannels({
  channels,
  onChange,
  machineName,
  pointLabel,
  machineCode,
  onBack,
  onNext,
}: {
  channels: ChannelDraft[];
  onChange: (c: ChannelDraft[]) => void;
  machineName: string;
  pointLabel: string;
  machineCode: string;
  onBack: () => void;
  onNext: () => void;
}) {
  const [points, setPoints] = useState<Point[]>([]);

  useEffect(() => {
    if (!machineCode) return;
    apiFetch<Point[]>(`/setup/points-list?machine_code=${machineCode}`)
      .then(setPoints)
      .catch(() => {});
  }, [machineCode]);

  const patch = (idx: number, p: Partial<ChannelDraft>) =>
    onChange(channels.map(c => (c.channel_index === idx ? { ...c, ...p } : c)));

  const anyEnabled = channels.some(c => c.enabled);

  return (
    <div className="max-w-lg">
      <h2 className="text-base font-semibold text-gray-800 mb-1">Which channels are wired?</h2>
      <p className="text-sm text-gray-500 mb-5">
        Enable each input that has something connected, and choose where its trend
        should appear on <span className="font-medium text-gray-700">{machineName}</span>.
      </p>

      <div className="space-y-2.5 mb-5">
        {channels.map(ch => (
          <div
            key={ch.channel_index}
            className={`rounded-xl border px-4 py-3 transition-colors ${
              ch.enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'
            }`}
          >
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={ch.enabled}
                onChange={e => patch(ch.channel_index, { enabled: e.target.checked })}
                className="w-4 h-4 accent-[#00D4BE]"
              />
              <span className={`text-sm font-semibold ${ch.enabled ? 'text-gray-800' : 'text-gray-400'}`}>
                Channel {ch.channel_index}
              </span>
            </label>

            {ch.enabled && (
              <div className="mt-3 pl-7 space-y-2">
                <label className="block text-xs text-gray-500 uppercase tracking-wide">Assign to</label>
                <div className="flex flex-wrap gap-2">
                  {([
                    ['point', `This sensor's point (${pointLabel})`],
                    ['other_point', 'Another point'],
                    ['machine', 'Machine level'],
                  ] as [ChannelAssign, string][]).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => patch(ch.channel_index, { assign: id, other_point_index: null })}
                      className={`px-3 py-1.5 rounded-lg border text-xs transition-colors ${
                        ch.assign === id
                          ? 'bg-[#00D4BE] text-white border-[#00D4BE]'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-[#00D4BE]/40'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {ch.assign === 'other_point' && (
                  <SelectRow
                    label="Point"
                    value={points.find(p => p.point_index === ch.other_point_index) ?? null}
                    options={points}
                    onSelect={p => patch(ch.channel_index, { other_point_index: p.point_index })}
                    getKey={p => String(p.point_index)}
                    getLabel={p => (p.point_name ? `${p.point_index} — ${p.point_name}` : `Point ${p.point_index}`)}
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          type="button"
          disabled={!anyEnabled || channels.some(c => c.enabled && c.assign === 'other_point' && c.other_point_index === null)}
          onClick={onNext}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors disabled:opacity-50"
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3b (GP) — variable, scaling, and the live test box
// ---------------------------------------------------------------------------
// THE TEST BOX CALLS THE SERVER. There is no linear-scaling arithmetic anywhere
// in this file, on purpose: a TypeScript copy of scale_signal_to_eng would be a
// second implementation of the canonical map, free to drift from the Python one
// that will later derive unit code 9061 from the stored 9060 raw. The operator
// would then be shown a number at configuration time that the database never
// agrees with. POST /setup/scale-preview is stateless and pure, and debounced it
// is one request when typing pauses — so the preview is the same function, by
// construction rather than by discipline.

function ScalePreviewBox({
  ch,
  onPatch,
}: {
  ch: ChannelDraft;
  onPatch: (p: Partial<ChannelDraft>) => void;
}) {
  const { testInput, in_min, in_max, eng_min, eng_max } = ch;

  const ready =
    testInput.trim() !== '' &&
    [in_min, in_max, eng_min, eng_max].every(v => v.trim() !== '' && !Number.isNaN(Number(v))) &&
    !Number.isNaN(Number(testInput));

  useEffect(() => {
    if (!ready) {
      onPatch({ preview: null, previewError: '', previewPending: false });
      return;
    }
    onPatch({ previewPending: true });
    // Debounced: one call when typing pauses, not one per keystroke.
    const t = setTimeout(() => {
      apiFetch<{ value: number }>('/setup/scale-preview', {
        method: 'POST',
        body: JSON.stringify({
          x: Number(testInput),
          in_min: Number(in_min), in_max: Number(in_max),
          eng_min: Number(eng_min), eng_max: Number(eng_max),
        }),
      })
        .then(r => onPatch({ preview: r.value, previewError: '', previewPending: false }))
        .catch((err: unknown) => onPatch({
          preview: null,
          previewError: err instanceof Error ? err.message : 'Preview failed.',
          previewPending: false,
        }));
    }, 350);
    return () => clearTimeout(t);
    // onPatch is recreated per render by the parent; depending on it would refire
    // the timer on every keystroke of every other field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testInput, in_min, in_max, eng_min, eng_max, ready]);

  return (
    <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1.5">
        Test — computed by the server, not the browser
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={testInput}
          onChange={e => onPatch({ testInput: e.target.value })}
          placeholder="signal in"
          className="w-28 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-[#00D4BE]/60"
        />
        <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
        <div className="flex-1 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-mono text-gray-800 min-h-[34px] flex items-center">
          {ch.previewError
            ? <span className="text-red-500 text-xs font-sans">{ch.previewError}</span>
            : ch.previewPending
              ? <span className="text-gray-300">…</span>
              : ch.preview !== null
                ? <span>{ch.preview} <span className="text-gray-400">{ch.eng_unit}</span></span>
                : <span className="text-gray-300">—</span>}
        </div>
      </div>
    </div>
  );
}

function NumField({
  label, value, onChange, suffix, placeholder,
}: {
  label: string; value: string; onChange: (v: string) => void;
  suffix?: string; placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</label>
      <div className="relative">
        <input
          type="number"
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-[#00D4BE]/60"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{suffix}</span>
        )}
      </div>
    </div>
  );
}

function StepVariables({
  channels,
  signalType,
  onChange,
  onBack,
  onSave,
  saving,
}: {
  channels: ChannelDraft[];
  signalType: SignalType;
  onChange: (c: ChannelDraft[]) => void;
  onBack: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  const isCurrent = signalType === 'current';
  const enabled = channels.filter(c => c.enabled);
  const patch = (idx: number, p: Partial<ChannelDraft>) =>
    onChange(channels.map(c => (c.channel_index === idx ? { ...c, ...p } : c)));

  const complete = enabled.every(c =>
    c.variable_name.trim() !== '' && c.eng_unit.trim() !== '' &&
    (isCurrent || [c.in_min, c.in_max, c.eng_min, c.eng_max]
      .every(v => v.trim() !== '' && !Number.isNaN(Number(v)))));

  return (
    <div className="max-w-lg">
      <h2 className="text-base font-semibold text-gray-800 mb-1">Name and scale each channel</h2>
      <p className="text-sm text-gray-500 mb-5">
        {isCurrent
          ? 'Current-clamp channels report Amps directly — no scaling required.'
          : 'Give each channel its variable name, engineering unit, and the range it maps to.'}
      </p>

      <div className="space-y-4 mb-5">
        {enabled.map(ch => (
          <div key={ch.channel_index} className="bg-white rounded-xl border border-gray-200 px-4 py-3.5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-semibold text-gray-800">Channel {ch.channel_index}</span>
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                {ch.assign === 'machine' ? 'machine level' :
                 ch.assign === 'other_point' ? `point ${ch.other_point_index}` : 'sensor point'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Variable</label>
                <input
                  type="text"
                  value={ch.variable_name}
                  placeholder="Discharge Pressure"
                  onChange={e => patch(ch.channel_index, { variable_name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00D4BE]/60"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Unit</label>
                <input
                  type="text"
                  value={ch.eng_unit}
                  placeholder={isCurrent ? 'A' : 'psi'}
                  onChange={e => patch(ch.channel_index, { eng_unit: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00D4BE]/60"
                />
              </div>
            </div>

            {!isCurrent && (
              <>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <NumField label="Signal min" value={ch.in_min}
                            suffix={signalType === '4-20mA' ? 'mA' : 'V'}
                            onChange={v => patch(ch.channel_index, { in_min: v })} />
                  <NumField label="Signal max" value={ch.in_max}
                            suffix={signalType === '4-20mA' ? 'mA' : 'V'}
                            onChange={v => patch(ch.channel_index, { in_max: v })} />
                  <NumField label="Value at min" value={ch.eng_min} suffix={ch.eng_unit}
                            onChange={v => patch(ch.channel_index, { eng_min: v })} />
                  <NumField label="Value at max" value={ch.eng_max} suffix={ch.eng_unit}
                            onChange={v => patch(ch.channel_index, { eng_max: v })} />
                </div>
                <ScalePreviewBox ch={ch} onPatch={p => patch(ch.channel_index, p)} />
              </>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !complete}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save Device'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Done state
// ---------------------------------------------------------------------------

function StepDone({
  phantomCode,
  machineName,
  pointName,
  mapping,
  sensorClass,
  channels,
  onAddAnother,
}: {
  phantomCode: string;
  machineName: string;
  pointName: string;
  mapping: AxisMapping;
  sensorClass: SensorClass;
  channels: ChannelDraft[];
  onAddAnother: () => void;
}) {
  return (
    <div className="max-w-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
          <Check className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-800">Device registered</h2>
          <p className="text-sm text-gray-500">Phantom {phantomCode}</p>
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg border border-gray-200 px-4 py-3 space-y-1.5 text-sm mb-5">
        <div className="flex justify-between">
          <span className="text-gray-500">Machine</span>
          <span className="text-gray-800 font-medium">{machineName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Point</span>
          <span className="text-gray-800 font-medium">{pointName}</span>
        </div>
        {sensorClass === 'vibration' ? (
          <div className="flex justify-between">
            <span className="text-gray-500">Axis map</span>
            <span className="text-gray-800 font-medium font-mono">X→{mapping.x} · Y→{mapping.y} · Z→{mapping.z}</span>
          </div>
        ) : (
          channels.filter(c => c.enabled).map(c => (
            <div key={c.channel_index} className="flex justify-between">
              <span className="text-gray-500">Channel {c.channel_index}</span>
              <span className="text-gray-800 font-medium">
                {c.variable_name} <span className="text-gray-400">({c.eng_unit})</span>
              </span>
            </div>
          ))
        )}
      </div>
      <button
        type="button"
        onClick={onAddAnother}
        className="px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors"
      >
        Add another device
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AddDevice() {
  const [step, setStep] = useState<Step>('type');
  const [sensorClass, setSensorClass] = useState<SensorClass>('vibration');
  const [signalType, setSignalType] = useState<SignalType>('0-10vdc');
  const [code, setCode] = useState('');
  const [location, setLocation] = useState<{
    db: DbEntry; company: Company; area: Area; machine: Machine; point: Point;
  } | null>(null);
  const [mapping, setMapping] = useState<AxisMapping>({ x: 'H', y: 'V', z: 'A' });
  const [channels, setChannels] = useState<ChannelDraft[]>(
    [1, 2, 3, 4].map(i => newChannel(i, '0-10vdc')));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The effective signal type: the Current Clamp class has exactly one, and it
  // is the class that decides it — not the 0-10/4-20 selector, which is only
  // shown for General Purpose.
  const effectiveSignal: SignalType = sensorClass === 'gp_current' ? 'current' : signalType;

  const reset = () => {
    setStep('type');
    setSensorClass('vibration');
    setSignalType('0-10vdc');
    setCode('');
    setLocation(null);
    setMapping({ x: 'H', y: 'V', z: 'A' });
    setChannels([1, 2, 3, 4].map(i => newChannel(i, '0-10vdc')));
    setError('');
  };

  // Re-seed the channel drafts when the class/signal changes, so the pre-filled
  // input range and unit match what was picked. Only touches untouched drafts'
  // defaults — the step is upstream of the variable step, so nothing typed is lost.
  const applyType = (cls: SensorClass, sig: SignalType) => {
    const eff: SignalType = cls === 'gp_current' ? 'current' : sig;
    setChannels([1, 2, 3, 4].map(i => newChannel(i, eff)));
  };

  const handleSave = async () => {
    if (!location) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch('/setup/devices', {
        method: 'POST',
        body: JSON.stringify({
          phantom_code: code.trim(),
          machine_code: location.machine.machine_code,
          point_index: location.point.point_index,
          point_name: location.point.point_name,
          db_id: location.db.id,
          axis_mapping: mapping,
        }),
      });
      setStep('done');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save device.');
    } finally {
      setSaving(false);
    }
  };

  // GP save: register the device, THEN write its channel config. Two calls in
  // this order because the PUT 404s on an unknown phantom_code — the device row
  // is what the config hangs off. The PUT itself is one transaction server-side
  // (backing axes row -> config row -> sensor_class), so a channel cannot land
  // half-configured; what a failure here can leave is a REGISTERED-but-
  // UNCONFIGURED device, which is visible in Review Devices and re-savable, not
  // a silent partial.
  const handleSaveGp = async () => {
    if (!location) return;
    setSaving(true);
    setError('');
    const enabled = channels.filter(c => c.enabled);
    const isCurrent = effectiveSignal === 'current';
    const num = (v: string) => (v.trim() === '' ? null : Number(v));
    try {
      await apiFetch('/setup/devices', {
        method: 'POST',
        body: JSON.stringify({
          phantom_code: code.trim(),
          machine_code: location.machine.machine_code,
          point_index: location.point.point_index,
          point_name: location.point.point_name,
          db_id: location.db.id,
          axis_mapping: mapping,
        }),
      });
      await apiFetch(`/setup/devices/${encodeURIComponent(code.trim())}/channels`, {
        method: 'PUT',
        body: JSON.stringify({
          channels: enabled.map(c => ({
            channel_index: c.channel_index,
            signal_type: effectiveSignal,
            variable_name: c.variable_name.trim(),
            eng_unit: c.eng_unit.trim(),
            enabled: true,
            // Current is already Amps on the wire: the server REFUSES scaling
            // values on a current channel, so send nulls rather than zeros.
            in_min:  isCurrent ? null : num(c.in_min),
            in_max:  isCurrent ? null : num(c.in_max),
            eng_min: isCurrent ? null : num(c.eng_min),
            eng_max: isCurrent ? null : num(c.eng_max),
            assign_to: c.assign === 'machine' ? 'machine' : 'point',
            point_index: c.assign === 'other_point' ? c.other_point_index : null,
          })),
        }),
      });
      setStep('done');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save channel configuration.';
      // The PUT is require_admin (matching POST /setup/devices). Say so plainly
      // rather than leaving the operator on a button that keeps failing.
      setError(msg === 'Admin access required'
        ? 'Admin access is required to save channel configuration. The device may have '
          + 'been registered — check Review Devices before retrying.'
        : msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-xl font-semibold text-gray-800 mb-6">Add Device</h1>

      {step !== 'done' && <StepBar current={step} steps={stepsFor(sensorClass)} />}

      {error && (
        <div className="mb-5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      {step === 'type' && (
        <StepType
          sensorClass={sensorClass}
          signalType={signalType}
          onChangeClass={c => { setSensorClass(c); applyType(c, signalType); }}
          onChangeSignal={s => { setSignalType(s); applyType(sensorClass, s); }}
          onNext={() => setStep('code')}
        />
      )}

      {step === 'code' && (
        <StepCode
          code={code}
          onChange={setCode}
          onNext={() => setStep('location')}
        />
      )}

      {step === 'location' && (
        <StepLocation
          onSelect={loc => {
            setLocation(loc);
            setStep(sensorClass === 'vibration' ? 'axes' : 'channels');
          }}
          onBack={() => setStep('code')}
        />
      )}

      {step === 'axes' && (
        <StepAxes
          mapping={mapping}
          onChange={setMapping}
          onBack={() => setStep('location')}
          onSave={handleSave}
          saving={saving}
        />
      )}

      {step === 'channels' && location && (
        <StepChannels
          channels={channels}
          onChange={setChannels}
          machineName={location.machine.machine_name}
          pointLabel={location.point.point_name ?? `Point ${location.point.point_index}`}
          machineCode={location.machine.machine_code}
          onBack={() => setStep('location')}
          onNext={() => setStep('variables')}
        />
      )}

      {step === 'variables' && (
        <StepVariables
          channels={channels}
          signalType={effectiveSignal}
          onChange={setChannels}
          onBack={() => setStep('channels')}
          onSave={handleSaveGp}
          saving={saving}
        />
      )}

      {step === 'done' && location && (
        <StepDone
          phantomCode={code}
          machineName={location.machine.machine_name}
          pointName={location.point.point_name ?? `Point ${location.point.point_index}`}
          mapping={mapping}
          sensorClass={sensorClass}
          channels={channels}
          onAddAnother={reset}
        />
      )}
    </div>
  );
}
